import { RouteSection, SectionLabel } from 'types';
import { ApplicationState } from '../index';
import { IApplyState } from './reducer';

// Base selector for apply state
const getApplyState = (state: ApplicationState): IApplyState => state.apply;

// Route status selectors for dynamic task configuration
export const getRouteStatusSections = (state: ApplicationState): RouteSection[] | null =>
  getApplyState(state).routeStatus.sections;

export const getRouteStatusLoading = (state: ApplicationState): boolean => getApplyState(state).routeStatus.isLoading;

export const getRouteStatusError = (state: ApplicationState): string | undefined =>
  getApplyState(state).routeStatus.error;

export const getApplicationSection = (state: ApplicationState): RouteSection | undefined =>
  getRouteStatusSections(state)?.find(s => s.label === SectionLabel.Application);

export const getApplicationTaskGroups = (state: ApplicationState) => getApplicationSection(state)?.taskGroups ?? [];

export const isTaskInRouteStatus = (state: ApplicationState, taskRoute: string): boolean => {
  const sections = getRouteStatusSections(state);
  if (!sections) return true; // Permissive fallback when not loaded
  return sections.some(section =>
    section.taskGroups?.some(group => group.tasks.some(task => task.route.includes(taskRoute))),
  );
};

/**
 * Normalizes a task route by removing the /applications/{applicationId} prefix.
 * Used to convert API route format to local route format.
 */
export const normalizeTaskRoute = (route: string): string => route.replace(/\/applications\/[^/]+/, '');

// Selector for document counts
export const getDocumentCounts = (state: ApplicationState) => getApplyState(state).documentCounts || {};

// Selector for all documents
export const getAllDocuments = (state: ApplicationState) => getApplyState(state).allDocuments || {};

// Selector for loading state of all documents
export const getLoadingAllDocuments = (state: ApplicationState): boolean =>
  getApplyState(state).loadingAllDocuments || false;

// Helper selector to get count for specific document types
export const getDocumentCountsForTypes = (state: ApplicationState, docTypes: string[]): number => {
  const documentCounts = getDocumentCounts(state);
  return docTypes.reduce((total, docType) => {
    return total + (documentCounts[docType] || 0);
  }, 0);
};

// Helper selector to check if document counts are available for specific types
export const areDocumentCountsAvailable = (state: ApplicationState, docTypes: string[]): boolean => {
  const documentCounts = getDocumentCounts(state);
  return docTypes.every(docType => Object.prototype.hasOwnProperty.call(documentCounts, docType));
};
